import { PropertyType } from '@/backend/types'
import { useEntityStore } from '@/data/useEntityStore'
import type { Field } from '@/modules/Project/Fields/types'
import type { Property } from '@/modules/Project/Properties/types'
import type { PartialRecord } from '@/shared/types'
import { getMillisecondsFromUUIDv7 } from '@/shared/utils'
import { defineStore } from 'pinia'
import { computed, type ComputedRef, type Ref, ref } from 'vue'
import type { Entity, View, ViewInfo } from './types'

const DEFAULT_COLUMN_WIDTH = 180

const fileURLForType = (
  field: Field,
  operation: 'display' | 'download',
  valueType: 'tool' | 'manual',
): string | null => {
  const isTool = valueType === 'tool'
  const property = isTool ? 'toolValue' : 'manualValue'
  const pdfProperty = isTool ? 'toolValuePDF' : 'manualValuePDF'

  if (field.type === 'file_collection') {
    if (Array.isArray(field[property])) {
      return field[property].at(0) || null
    }
    return field[property]
  }

  if (field.type === 'file' || field.type === 'data') {
    if (operation === 'display' && field[pdfProperty]) {
      return field[pdfProperty]
    }

    return field[property]
  }

  return null
}

export const fileURL = (field: Field, operation: 'display' | 'download') =>
  fileURLForType(field, operation, 'manual') ?? fileURLForType(field, operation, 'tool')

export const useProjectStore = defineStore('project', () => {
  const entityStore = useEntityStore()
  const projectId = ref<string | null>(null)
  const sort = ref<{ prop: Property['id']; order: 'asc' | 'desc' } | null>(null)

  const selectedEntityIds: Ref<Set<Entity['id']>> = ref(new Set<Entity['id']>())
  const properties: Ref<Property[]> = ref([])
  const projectLoaded = ref(false)
  const entityIdsPerView: Ref<Map<View['id'], (string | undefined)[]>> = ref(new Map())
  const views: Ref<View[]> = ref([])
  const widths: Ref<Partial<Record<Property['id'], number>>> = ref({})
  const activeViewId = ref<View['id'] | null>(null)
  const mainViewId = ref<View['id'] | null>(null)
  const areEntitiesStale = ref(false)
  const parentEntityId = ref<string | null>(null)

  /**
   * We have optimistic UI when creating properties. This ref stores
   * the ids of properties that are 'placeholders' and are currently
   * being created.
   */
  const optimisticIds = ref<string[]>([])

  /**
   * Maps each Entity ID to its draft field values. These are the values shown when
   * previewing ranges of cells when pasting. These are stored separately to the
   * entities because entities are often reloaded when the view changes, which
   * would cause draft values to be lost (unless we did an expensive lookup
   * to persist draft data when loading entities).
   */
  const draftFields = ref<
    PartialRecord<Entity['id'], PartialRecord<Property['id'], Field['manualValue']>>
  >({})
  const resetDrafts = () => {
    draftFields.value = {}
  }

  const setParentEntityId = (pid?: string) => {
    parentEntityId.value = pid ?? null
  }

  const setEntitiesStale = (stale: boolean) => (areEntitiesStale.value = stale)

  const entityIdToIndexPerView: ComputedRef<Map<string, Map<string, number>>> = computed(() => {
    const result = new Map()
    entityIdsPerView.value.forEach((entityIds, viewId) =>
      result.set(viewId, new Map(entityIds.flatMap((e, i) => (e ? [[e, i]] : [])))),
    )
    return result
  })

  const viewInfo = (viewId: string): ViewInfo | undefined => {
    const view = views.value.find((v) => v.id === viewId)

    if (!view) return undefined

    const entityIds = entityIdsPerView.value.get(viewId)
    const entityIdToIndex = entityIdToIndexPerView.value.get(viewId)
    if (!entityIds || !entityIdToIndex) {
      return {
        id: viewId,
        view,
        entities: undefined,
        entityIds: undefined,
        entityIdToIndex: undefined,
      }
    }

    const entities = entityIds.map((id) =>
      id ? entityStore.getFullOrPartialEntity(id) : undefined,
    )

    return {
      id: viewId,
      view,
      entities,
      entityIds,
      entityIdToIndex,
    }
  }

  const activeView = computed(() => (activeViewId.value ? viewInfo(activeViewId.value) : undefined))
  const mainView = computed(() => (mainViewId.value ? viewInfo(mainViewId.value) : undefined))

  const getWidth = (id: string) => widths.value[id] ?? DEFAULT_COLUMN_WIDTH

  const resizeProperty = (id: Property['id'], width: number) => {
    widths.value[id] = width
  }

  const spliceEntities = (
    incomingEntities: Entity[],
    totalCountFromServer: number | null,
    start: number,
    viewId: string,
  ) => {
    let entityIds = entityIdsPerView.value.get(viewId) ?? []

    if (typeof totalCountFromServer === 'number' && totalCountFromServer !== entityIds.length) {
      // if the total count has changed, we need to update the count in the store
      entityIds = Array.from({ length: totalCountFromServer })
    }

    entityIds.splice(start, incomingEntities.length, ...incomingEntities.map((e) => e.id))
    entityIdsPerView.value.set(viewId, entityIds)
  }

  const dropEntitiesOutsideOfView = (viewId: string, start: number, end: number) => {
    const info = viewInfo(viewId)

    if (!info || !info.entities) {
      entityIdsPerView.value = new Map()
      return
    }

    info.entities = info.entities.fill(undefined, 0, start).fill(undefined, end)
    entityIdsPerView.value = new Map([[info.id, info.entities.map((e) => e?.id)]])
  }

  /** Sets the views and resets the entitiesPerView array to be empty */
  const setViews = (newMainViewId: string | null, newViews: View[]) => {
    views.value = newViews
    mainViewId.value = newMainViewId
    activeViewId.value = newMainViewId
    entityIdsPerView.value = new Map()
    areEntitiesStale.value = false
    resetDrafts()
  }

  const upsertView = (view: View) => {
    const index = views.value.findIndex((v) => v.id === view.id)
    if (index >= 0) {
      views.value.splice(index, 1, { ...views.value[index], ...view })
    } else {
      views.value.push(view)
    }
  }

  const removeView = (id: string) => {
    views.value = views.value.filter((v) => v.id !== id)
    entityIdsPerView.value.get(id)?.forEach((entityId) => {
      if (!entityId) return
      removeEntityFromView(entityId, id)
    })
    entityIdsPerView.value.delete(id)
    if (activeViewId.value == id) {
      activeViewId.value = mainViewId.value
    }
  }

  /**
   * Pushes or replaces an entity id reference in each relevant view
   *
   * This action ensures it happens correctly, and we don't insert duplicates.
   * This also updates the entities within each view if the entity is active in that view.
   */
  const upsertEntity = (entity: Entity) => {
    views.value.forEach((view) => {
      if (
        view.id === activeViewId.value &&
        parentEntityId.value !== null &&
        entity.parentEntityId !== parentEntityId.value
      ) {
        return
      }

      const isPartOfView = entity.activeViewIds?.includes(view.id)
      const info = viewInfo(view.id)
      if (!info || !info.entityIds) {
        return
      }
      const index = info.entityIdToIndex.get(entity.id) ?? -1
      if (!isPartOfView) {
        // if it is not part and is there then remove it
        if (index >= 0) info.entityIds.splice(index, 1)
        return
      }
      // if it is part and is there then update it
      if (index >= 0) {
        info.entityIds.splice(index, 1, entity.id)
        return
      }

      // if it is part and is not there - we need to find where to insert it,
      if (isPartOfView && index === -1) {
        // we can insert it only if that entity is somewhere between the entities, if it's position is next to an undefined entity then we can't insert it

        const sortedArray = [...info.entities.filter((x): x is Entity => !!x), entity].toSorted(
          (a, b) => getMillisecondsFromUUIDv7(a.id) - getMillisecondsFromUUIDv7(b.id),
        )
        const index = sortedArray.findIndex((e) => e.id === entity.id)
        const leftNeighbour = sortedArray[index - 1]
        const rightNeighbour = sortedArray[index + 1]

        const leftNeighbourIndex = info.entityIdToIndex.get(leftNeighbour?.id || '') ?? -1
        const rightNeighbourIndex = info.entityIdToIndex.get(rightNeighbour?.id || '') ?? -1

        const rightNeighbourIsFirst = rightNeighbourIndex === 0
        if (rightNeighbourIsFirst) {
          info.entityIds.unshift(entity.id)
          return
        }
        const leftNeighbourIsLast = leftNeighbourIndex === info.entities.length - 1
        if (leftNeighbourIsLast) {
          info.entityIds.push(entity.id)
          return
        }
        const neighboursAreNextToEachOther = leftNeighbourIndex + 1 === rightNeighbourIndex
        if (neighboursAreNextToEachOther) {
          info.entityIds.splice(rightNeighbourIndex, 0, entity.id)
          return
        }
      }
    })
  }

  const removeEntityFromView = (entityId: string, viewId: string) => {
    const entity = entityStore.getFullEntity(entityId)
    if (entity && !entity.activeViewIds?.includes(viewId)) {
      entity.activeViewIds = entity.activeViewIds?.filter((id) => id !== viewId)
    }

    const partialEntity = entityStore.getPartialEntity(entityId)
    if (partialEntity && !partialEntity.activeViewIds?.includes(viewId)) {
      partialEntity.activeViewIds = partialEntity.activeViewIds?.filter((id) => id !== viewId)
      upsertEntity(partialEntity)
    }
  }

  // TODO update existing logic to use this map instead of properties.find(p => p.id === someid)
  const propertiesById = computed<Partial<Record<string, Property>>>(() =>
    Object.fromEntries(properties.value.map((p) => [p.id, p])),
  )

  const visibleProperties = computed(() => {
    return (activeView.value?.view.propertyIds ?? [])
      .map((id) => propertiesById.value[id])
      .filter((x): x is Property => !!x)
  })

  const setProperties: (p: Property[]) => void = (p) => {
    properties.value = p
  }

  const shiftPropertyToIndex = (propertyId: string, newIndex: number) => {
    const view = activeView.value?.view
    if (!view || !view.propertyIds) return { ok: false }
    const oldPropertyIds = visibleProperties.value.map((x) => x.id)
    const newPropertyIds = oldPropertyIds.slice()
    const propertyIndex = newPropertyIds.findIndex((id) => id === propertyId)
    if (propertyIndex === -1) return { ok: false }
    if (propertyIndex === newIndex) return { ok: false }
    newPropertyIds.splice(propertyIndex, 1)
    newPropertyIds.splice(newIndex, 0, propertyId)
    upsertView({ ...view, propertyIds: newPropertyIds })
    return { ok: true, oldPropertyIds } as const
  }

  const upsertProperty = (property: Property) => {
    const index = properties.value.findIndex((p) => p.id === property.id)
    if (index >= 0) {
      properties.value.splice(index, 1, property)
    } else {
      properties.value.push(property)
    }
  }

  const removeProperty = (id: string) => {
    properties.value = properties.value.filter((p) => p.id !== id)
    entityIdsPerView.value.forEach((entityIds) =>
      entityIds.forEach((entityId) => {
        if (!entityId) return

        const entity = entityStore.getFullEntity(entityId)
        if (entity) {
          entity.fields.delete(id)
        }

        const partialEntity = entityStore.getPartialEntity(entityId)
        if (partialEntity) {
          partialEntity.fields.delete(id)
        }
      }),
    )
  }

  const subProjectIds = computed(() => {
    const subProjectIds = properties.value
      .map((p) => {
        // Currently only PDF and Collection properties have subprojects, in the future there will be more
        if (p.type === PropertyType.collection || p.type === PropertyType.file_collection) {
          return p.config.subprojectConfig.child_project_id
        }
        return null
      })
      .filter((p): p is string => p !== null)

    return [...new Set(subProjectIds)]
  })

  const toggleEntity = (rowId: string, value: boolean) => {
    if (value) {
      selectedEntityIds.value.add(rowId)
    } else {
      selectedEntityIds.value.delete(rowId)
    }
  }

  const toggleAllEntities = () => {
    if (!activeView.value || !activeView.value.entities) return
    const entities = activeView.value.entities

    if (selectedEntityIds.value.size < entities.length) {
      entities.forEach((r) => r && selectedEntityIds.value.add(r.id))
    } else {
      selectedEntityIds.value.clear()
    }
  }

  const clearAllSelectedEntities = () => {
    selectedEntityIds.value.clear()
  }

  const extendRowSelectionTo = (entityId: string) => {
    if (!activeView.value || !activeView.value.entities || !activeView.value.entityIdToIndex) return
    if (selectedEntityIds.value.size === 0) {
      selectedEntityIds.value.add(entityId)
      return
    }
    const { entities, entityIdToIndex } = activeView.value
    const targetIndex = entityIdToIndex.get(entityId)
    if (targetIndex === undefined) return

    const firstCheckedIndex = entities.findIndex((e) => e && selectedEntityIds.value.has(e.id))
    const lastCheckedIndex = entities.findLastIndex((e) => e && selectedEntityIds.value.has(e.id))

    const from = Math.min(lastCheckedIndex, targetIndex)
    const to = Math.max(firstCheckedIndex, targetIndex)

    entities.slice(from, to + 1).forEach((e) => e && selectedEntityIds.value.add(e.id))
  }

  const allEntitiesSelected = computed(
    () => selectedEntityIds.value.size === activeView.value?.entities?.length,
  )

  const setProjectId = (id: string) => {
    projectId.value = id
  }

  const setActiveViewId = (id?: string) => {
    activeViewId.value = id ?? mainViewId.value
  }

  const removeEntitiesById = (ids: string[]) => {
    const idSet = new Set(ids)
    ids.forEach((id) => selectedEntityIds.value.delete(id))

    views.value.forEach((view) => {
      const entityIds = entityIdsPerView.value.get(view.id)
      if (!entityIds) return
      entityIdsPerView.value.set(
        view.id,
        entityIds.filter((id) => id && !idSet.has(id)),
      )
    })
  }

  /**
   * Effectively acts as optimistic UI for setting field right after a user
   * updating it with the backend. Soon after, we will be receiving the full
   * field payload via websockets, at which point, whatever
   * we do here will be overriden.
   *
   * The goal here is to figure out whether to set the input value, or the corrected value.
   */
  const updateField = (updatedField: Field) => {
    const entity = entityStore.getFullEntity(updatedField.entityId)
    if (entity) {
      entity.fields.set(updatedField.propertyId, updatedField)
    }

    const partialEntity = entityStore.getPartialEntity(updatedField.entityId)
    if (partialEntity) {
      partialEntity.fields.set(updatedField.propertyId, updatedField)
    }
  }

  const setFileFieldValue = ({
    entityId,
    propertyId,
    value,
    filename,
  }: {
    entityId: string
    propertyId: string
    value: string
    filename: string
  }) => {
    const entity = entityStore.getFullEntity(entityId)
    if (entity) {
      const field = entity.fields.get(propertyId)
      if (!field) return

      if (field.type === 'file') {
        field.manualValue = value
        field.manualFilename = filename
        updateField(field)
      }
    }

    const partialEntity = entityStore.getPartialEntity(entityId)
    if (partialEntity) {
      const field = partialEntity.fields.get(propertyId)
      if (!field) return

      if (field.type === 'file') {
        field.manualValue = value
        field.manualFilename = filename
        updateField(field)
      }
    }
  }

  return {
    activeView,
    allEntitiesSelected,
    areEntitiesStale,
    clearAllSelectedEntities,
    dropEntitiesOutsideOfView,
    extendRowSelectionTo,
    getWidth,
    mainView,
    optimisticIds,
    projectId,
    projectLoaded,
    properties,
    propertiesById,
    removeEntitiesById,
    removeEntityFromView,
    removeProperty,
    removeView,
    resizeProperty,
    selectedEntityIds,
    setActiveViewId,
    setEntitiesStale,
    setFileFieldValue,
    setProjectId,
    setProperties,
    setViews,
    sort,
    spliceEntities,
    shiftPropertyToIndex,
    subProjectIds,
    toggleAllEntities,
    toggleEntity,
    updateField,
    upsertEntity,
    upsertProperty,
    upsertView,
    views,
    viewInfo,
    visibleProperties,
    widths,
    setParentEntityId,
    draftFields,
    resetDrafts,
  }
})
